/**
 * Shared helper for surfacing axios errors with the right tone.
 *
 * Backend convention (May 2026):
 *   • 502 = email provider failed (ACS / SMTP) — the action OFTEN
 *           succeeded server-side, only the notification email failed.
 *   • 503 / 500 = transient upstream issue — try again later.
 *   • 429 = rate-limited.
 *   • 4xx = real client error — backend's `detail` explains what.
 *   • no status (network) = tunnel down / phone offline.
 *
 * Usage:
 *   try {
 *     await api.createBooking(...);
 *   } catch (e) {
 *     toast(describeApiError(e, 'Failed to create booking'));
 *   }
 *
 * The returned message is end-user-safe. Pair with toast or alert.
 */
/**
 * Coerce whatever the backend put in `detail` / `message` / `error` into a
 * single human-readable string. FastAPI + Pydantic return Pydantic
 * validation errors as an ARRAY of `{type, loc, msg, input}` objects —
 * rendering that array directly as a React child throws
 * "Objects are not valid as a React child". This helper handles every
 * shape we've ever seen come back from the API:
 *
 *   - string                       → as-is
 *   - { msg: "..."}                → "..."
 *   - { detail: "..." }            → "..."
 *   - [ {msg}, {msg} ]             → "msg1; msg2"
 *   - [ "a", "b" ]                 → "a; b"
 *   - any other object             → JSON.stringify with a sane cap
 *   - null / undefined / ""        → null (caller falls back)
 */
function _detailToString(raw: any): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    const parts = raw
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          // Pydantic 2 errors: prefer msg + the field path (loc) so the
          // user knows WHICH field is wrong.
          const msg  = (item as any).msg  || (item as any).message;
          const loc  = (item as any).loc;
          if (msg) {
            if (Array.isArray(loc) && loc.length > 1) {
              // loc = ["body", "field_name"] → "field_name: msg"
              return `${loc.slice(1).join('.')}: ${msg}`;
            }
            return String(msg);
          }
        }
        try { return JSON.stringify(item); } catch { return String(item); }
      })
      .filter(Boolean);
    const joined = parts.join('; ');
    return joined.length > 240 ? joined.slice(0, 240) + '…' : joined;
  }
  if (typeof raw === 'object') {
    const msg = (raw as any).msg || (raw as any).message || (raw as any).detail;
    if (typeof msg === 'string') return msg;
    try {
      const s = JSON.stringify(raw);
      return s.length > 240 ? s.slice(0, 240) + '…' : s;
    } catch { return null; }
  }
  return String(raw);
}

export function describeApiError(err: any, fallback = 'Something went wrong'): string {
  const status = err?.response?.status;
  const detail =
    _detailToString(err?.response?.data?.detail)
    || _detailToString(err?.response?.data?.message)
    || _detailToString(err?.response?.data?.error);

  if (!status) {
    // No response object — usually a network failure.
    if (err?.code === 'ECONNABORTED') return 'Server timed out. Please try again.';
    return 'Cannot reach server. Check your connection and try again.';
  }
  if (status === 401) return 'Your session expired. Please sign in again.';
  if (status === 403) return detail || 'You don’t have permission for this action.';
  if (status === 404) return detail || 'The item you’re looking for is no longer available.';
  if (status === 409) return detail || 'This conflicts with an existing record.';
  if (status === 422) return detail || 'Some fields are invalid. Please check the form.';
  if (status === 429) return 'Too many requests. Please wait a minute and try again.';
  if (status === 502) {
    // Email provider failure — the action may have succeeded; only the
    // notification email failed.
    return `Action saved, but the notification email failed: ${detail || 'unknown email error'}`;
  }
  if (status >= 500) return `Server error (${status}). Please try again shortly.`;
  return detail || fallback;
}

/**
 * Convenience: returns true if the error is specifically a 502 email
 * delivery failure. Use to decide whether to show a softer toast
 * ("saved, but…") rather than a hard failure.
 */
export function isEmailDeliveryFailure(err: any): boolean {
  return err?.response?.status === 502;
}
